import React, { useState } from 'react';
import { MagnifyingGlass, SidebarSimple, CaretLeft, SignOut } from '../../design-system/icons';
import { Link } from '@tanstack/react-router';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { SimpleTooltip } from '../ui/tooltip';

export interface NavItem {
  id: string;
  /** Optional sub-view within a campaign tab. */
  sub?: string;
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  /** Renders a numeric badge — except in the stacked (dark + permanently
   *  collapsed) rail, where it collapses to a plain dot; no consumer of that
   *  rail sets `count` today, so a real number there is unimplemented. */
  count?: number;
  hide?: boolean;
  isPlaceholder?: boolean;
  /** If provided, renders as a Link from @tanstack/react-router instead of a button */
  href?: string;
  /** Extra path prefixes that mark this item active, beyond `href`. Use when a
   *  section owns routes that don't share the href prefix — e.g. a Campaigns
   *  item (href `/campaigns`) that must also light up on the singular
   *  campaign-detail route `/campaign/<slug>`. Matched via `startsWith`. */
  activePaths?: string[];
  /** Visual indentation for sub-items */
  indent?: boolean;
  /** Renders a horizontal divider bar directly above this item (Figma nav group split). */
  dividerAbove?: boolean;
  /** Small group heading shown by campaign navigation. */
  groupLabel?: string;
  /** Icon beside `groupLabel` in the campaign navigation's group header. */
  groupIcon?: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  /** Clarifies why a disabled placeholder is unavailable. */
  placeholderNote?: string;
}

export interface SidebarPrimaryAction {
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  onClick: () => void;
}

export interface AppSidebarProps {
  navItems: NavItem[];
  activeTab: string;
  /** Called when a button-type nav item is clicked. tabId matches item.id. */
  onTabChange?: (tabId: string) => void;
  onNavigateHome: () => void;
  userInitials: string;
  /** undefined renders a skeleton while profile data is loading. */
  userName: string | undefined;
  /** undefined renders a skeleton while profile data is loading. */
  userRole: string | undefined;
  onNavigateProfile: () => void;
  onSignOut: () => void;
  /** Back navigation callback (optional) */
  onNavigateBack?: () => void;
  backLabel?: string;
  onRefresh?: () => void;
  /** URL to the full wordmark logo (required) */
  logoSrc: string;
  /** URL to the mark-only logo shown when collapsed (falls back to logoSrc) */
  logoMarkSrc?: string;
  /** Display name shown next to the logo */
  appName: string;

  // ── Collapsible (Figma "Expand Nav" / "Collabs Nav") ──────────────────────
  /** Collapsed = icons-only rail (64px). Owned by AppLayout so it can offset main. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Attached to the logo button, which doubles as the expand toggle. */
  expandToggleRef?: React.Ref<HTMLButtonElement>;
  /** Attached to the rail container so the narrow overlay can trap Tab focus in it. */
  overlayRef?: React.Ref<HTMLElement>;

  // ── Optional Figma chrome (apps opt in; admin can omit) ───────────────────
  /** Renders the search field/button when provided. */
  onSearch?: () => void;
  searchPlaceholder?: string;
  /** Primary CTA pinned above the footer (e.g. "Create Campaign"). Renders
   *  icon-only + hover tooltip when merely collapsed — not yet adapted for
   *  the stacked (dark + permanently collapsed) rail, since no consumer of
   *  that rail sets this today. */
  primaryAction?: SidebarPrimaryAction;
  /** Secondary nav rendered in the footer (e.g. Settings, Help Centre). */
  footerItems?: NavItem[];

  /** Visual theme of the rail. Defaults to 'light' (today's unchanged stone
   *  chrome). 'dark' is the admin-only near-black rail from Figma. */
  variant?: 'light' | 'dark';
}

/**
 * App sidebar — collapsible icon rail (64px) ↔ expanded (224px), Figma "Expand
 * Nav" / "Collabs Nav".
 *
 * Collapse model (shadcn-style, replicated for smoothness): EVERY row uses one
 * pattern — a fixed 40px icon zone on the left (the glyph is centred, so it
 * lands at the same 32px in both states and NEVER moves) followed by a label
 * that simply CLIPS as the rail narrows (overflow-hidden). No opacity fades, no
 * justify-content / padding swaps, no conditional text. Only the rail width
 * animates (200ms ease-linear), which clips/reveals every label uniformly.
 */
const EASE = 'ease-[cubic-bezier(0.32,0.72,0,1)]';
const RAIL_TRANSITION = `transition-[width] duration-[260ms] ${EASE} will-change-[width]`;
/** Fixed icon zone — 40px, glyph centred → 32px (centre of the 64px rail).
 *  Also exactly fills the row (64 − 2×12 padding = 40), so nothing overflows. */
const ICON_ZONE = 'flex w-10 shrink-0 items-center justify-center';

export function AppSidebar(props: AppSidebarProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const collapsed = !!props.collapsed;
  const isDark = props.variant === 'dark';
  const markSrc = props.logoMarkSrc ?? props.logoSrc;
  // Labels fade in/out (in addition to clipping) on the same curve as the rail.
  const labelFade = `transition-opacity duration-[260ms] ${EASE} ${collapsed ? 'opacity-0' : 'opacity-100'}`;
  // The permanently-fixed admin rail (Figma "Admin main side bar"): dark +
  // collapsed always renders icon-over-label, never the light rail's
  // icon-only + hover-tooltip pattern — collapsed here isn't a transient
  // state to peek past, it's the only state.
  const stacked = isDark && collapsed;

  // Collapsed: clicking the empty rail (anywhere that isn't a real control)
  // expands it, so users aren't forced to hit the small toggle. Buttons/links
  // keep their own behaviour; the toggle + logo stay the keyboard path.
  const handleRailExpand = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, a, input, [role="button"]')) return;
    props.onToggleCollapse?.();
  };

  // ── Nav item (shared by primary nav + footer items) ─────────────────────────
  const renderNavItem = (tab: NavItem) => {
    if (tab.hide) return null;
    const isActive = props.activeTab === tab.id;
    const TabIcon = tab.icon;

    const className = stacked
      ? [
          // Figma "Admin main side bar": the highlight lives on the 42px icon
          // chip only (see `inner` below) — the item itself carries no
          // background/padding of its own, just the icon+label stack.
          'group relative flex w-full shrink-0 cursor-pointer flex-col items-center gap-1',
          'border-none text-center font-sans no-underline transition-colors duration-150',
          isActive ? 'text-white' : 'text-(--foreground-subtle) hover:text-(--foreground-primary-default)',
        ].join(' ')
      : [
          'relative flex h-10 w-full shrink-0 cursor-pointer items-center overflow-hidden rounded-lg',
          'border-none text-left font-sans text-body-md-regular font-medium no-underline transition-colors duration-150',
          isDark
            ? // Dark rail (admin only, Figma "Admin main side bar"): active = #404040
              // pill + #fafafa icon/label. Inactive = #a3a3a3, hover = the active
              // pill's own color at half opacity (a lighter overlay without a new hex).
              isActive
              ? 'bg-(--foreground-secondary-default) text-(--foreground-primary-default)'
              : 'text-(--foreground-subtle) hover:bg-(--foreground-secondary-default)/50 hover:text-(--foreground-primary-default)'
            : // Active = #ECEBE9 (stone-200) pill + #171717 (foreground) icon/label. Inactive = muted.
              isActive
              ? 'bg-stone-200 text-foreground'
              : 'text-muted-foreground hover:bg-stone-200/60 hover:text-foreground',
        ].join(' ');

    // Stacked (Figma "Sidebar / Nav Item"): icon over an always-visible 12px
    // label — no clipping/fade, no tooltip needed. Count shows only as the
    // small dot (a numeric pill doesn't fit this narrow, centered format).
    const inner = stacked ? (
      <>
        {/* Figma "Icon Chip": 42px square, 8px radius, the ONLY element that
            takes the active/hover background — the label below stays bare. */}
        <span
          className={`flex size-[42px] shrink-0 items-center justify-center rounded-lg transition-colors duration-150 ${
            isActive ? 'bg-(--foreground-secondary-default)' : 'group-hover:bg-(--foreground-secondary-default)/50'
          }`}
        >
          <TabIcon size={24} strokeWidth={1.6} />
        </span>
        {/* Words never break mid-word: a single word stays on one line, a
            multi-word label ("Shopify stores") wraps between words. The rail's
            8px padding leaves 53px, too narrow for "Payments" at 12px, so the
            label alone reaches 4px into it each side (61px). */}
        <span className="-mx-1 self-stretch text-center text-caption-regular/4 font-medium break-normal">{tab.label}</span>
        {tab.count !== undefined && tab.count > 0 && (
          <span className="absolute top-1 right-2 size-2 rounded-full bg-destructive" />
        )}
      </>
    ) : (
      <>
        <span className={ICON_ZONE}>
          <TabIcon size={20} strokeWidth={1.6} />
        </span>
        <span className={`pointer-events-none whitespace-nowrap leading-none ${labelFade}`}>{tab.label}</span>
        {tab.count !== undefined && tab.count > 0 ? (
          <>
            <span className="ml-auto mr-3 inline-flex min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 py-0.5 text-caption-medium text-white">
              {tab.count}
            </span>
            {collapsed && <span className="absolute top-1.5 right-2 size-2 rounded-full bg-destructive" />}
          </>
        ) : null}
      </>
    );

    let node: React.ReactNode;
    if (tab.href && !tab.isPlaceholder) {
      node = (
        <Link key={tab.id} to={tab.href} className={className} aria-current={isActive ? 'page' : undefined}>
          {inner}
        </Link>
      );
    } else {
      // Plain <button> (NOT shadcn Button — its cn()/twMerge injects padding/
      // justify and breaks the shared layout, e.g. the Integration placeholder).
      node = (
        <button
          key={tab.id}
          type="button"
          className={className}
          aria-current={isActive ? 'page' : undefined}
          style={tab.isPlaceholder ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
          onClick={(e) => {
            if (tab.isPlaceholder) {
              e.preventDefault();
              return;
            }
            props.onTabChange?.(tab.id);
          }}
        >
          {inner}
        </button>
      );
    }

    // Collapsed icons need a label tooltip (avoid mystery-meat nav) — except
    // the stacked layout, whose label is already always visible.
    const tip = tab.isPlaceholder ? `${tab.label} · Coming soon` : collapsed && !stacked ? tab.label : null;
    const item = tip ? (
      <SimpleTooltip content={tip} side="right">
        {node}
      </SimpleTooltip>
    ) : (
      node
    );

    // Figma nav-group split: a thin bar above the item (e.g. above Details, so
    // Details + Settings read as a bottom cluster). The -mt-1 pulls it toward
    // the item so the divider hugs the group it introduces rather than sitting
    // dead-centre in the gap-2 stack.
    if (tab.dividerAbove) {
      return (
        <React.Fragment key={tab.id}>
          <div
            className={`my-1 h-px w-full shrink-0 ${isDark ? 'bg-(--stone-700)' : 'bg-border'}`}
            aria-hidden
          />
          {item}
        </React.Fragment>
      );
    }
    // Non-fragment path still needs the key on the item itself.
    return <React.Fragment key={tab.id}>{item}</React.Fragment>;
  };

  return (
    <aside
      ref={props.overlayRef}
      className={[
        'fixed inset-y-0 left-0 z-20 flex h-screen flex-col overflow-hidden',
        // Dark rail (admin, Figma "Admin main side bar"): #171717 fill. The
        // light-mode border-border hairline (#e5e5e5) reads as a bright stray
        // line against it, so the dark variant reuses the divider color instead.
        // `!` is required: globals.css has an unlayered
        // `* { border-color: var(--border) }` that beats any @layer-utilities
        // class regardless of specificity — without it this silently rendered
        // the light #e5e5e5 border instead of stone-700 (confirmed live on
        // the composer's identical bug, see ThreadComposer.tsx).
        isDark ? 'border-r border-(--stone-700)! bg-(--foreground-default)' : 'border-r border-border bg-stone-50',
        RAIL_TRANSITION,
        collapsed ? 'cursor-pointer' : '',
        // Figma "Admin main side bar" spec: 69px wide (re-measured 2026-09-29,
        // supersedes the earlier 96px pixel-match — the Figma component itself
        // shrank) — distinct from the plain light collapsed rail's unrelated
        // 64px spec.
        stacked ? 'w-[69px]' : collapsed ? 'w-[64px]' : 'w-56',
      ].join(' ')}
      aria-label="Application navigation"
      onClick={collapsed && props.onToggleCollapse ? handleRailExpand : undefined}
      // Public (no-login) share view: the shell intercepts every click that
      // isn't inside a `[data-public-allowed]` subtree and turns it into a
      // login prompt. Collapsing/expanding the rail is pure layout — no data,
      // no navigation — so the click-to-expand CONTAINER is exempt while
      // collapsed (that's when a bare rail click expands it). Inert everywhere
      // else. See campaign-detail/PublicCampaignShell.tsx.
      {...(collapsed && props.onToggleCollapse ? { 'data-public-allowed': '' } : {})}
    >
      {/* ── Logo + collapse control ─────────────────────────────────────────
          Collapsed: the logo mark doubles as the expand toggle — on hover the
          mark swaps to the sidebar icon, click expands (the toggle lives BEHIND
          the logo; the 72px rail has no room for a separate button).
          Expanded: the logo is just the logo (→ home); a dedicated, always-
          visible collapse button sits on the right of the header. */}
      <div
        className={
          // Figma "Admin main side bar": 16px padding-top, 8px horizontal
          // (not the light rail's fixed 52px row + 12px horizontal), 12px gap
          // to the divider below.
          stacked
            ? 'flex shrink-0 items-center justify-center overflow-hidden px-2 pt-4 pb-3'
            : 'flex h-[52px] shrink-0 items-center overflow-hidden px-3'
        }
      >
        <button
          type="button"
          ref={props.expandToggleRef}
          onClick={collapsed ? (props.onToggleCollapse ?? props.onNavigateHome) : props.onNavigateHome}
          className={`group/logo relative flex shrink-0 items-center justify-center ${stacked ? 'size-[46px]' : 'w-10'}`}
          aria-label={collapsed && props.onToggleCollapse ? 'Expand sidebar' : props.appName}
          // Exempt from the public share view's click interception ONLY while
          // it is the expand toggle; expanded it navigates home, which must
          // still prompt login.
          {...(collapsed && props.onToggleCollapse ? { 'data-public-allowed': '' } : {})}
        >
          <img
            src={markSrc}
            alt={props.appName}
            // Figma "Admin main side bar": the logo mark renders at 46px there,
            // vs the plain rail's 32px (size-8).
            className={`${stacked ? 'size-[46px]' : 'size-8'} shrink-0 object-contain transition-opacity duration-150 ${collapsed && props.onToggleCollapse ? 'group-hover/logo:opacity-0' : ''}`}
          />
          {collapsed && props.onToggleCollapse && (
            <SidebarSimple
              size={20}
              strokeWidth={1.6}
              className={`absolute opacity-0 transition-opacity duration-150 group-hover/logo:opacity-100 ${isDark ? 'text-(--foreground-primary-default)' : 'text-foreground'}`}
            />
          )}
        </button>
        {/* Stacked rail: this label is always invisible (opacity-0 via
            labelFade, never toggled on — there's no expand control to ever
            reveal it) — but as a flex-1 filler it was stealing the row's
            free space and shoving the logo left instead of letting
            `justify-center` above center it. `hidden` removes its footprint;
            the logo button's own aria-label already carries the accessible
            name in this mode. */}
        {!stacked && (
          <button
            type="button"
            onClick={props.onNavigateHome}
            tabIndex={collapsed ? -1 : 0}
            aria-label={props.appName}
            className={`min-w-0 flex-1 overflow-hidden text-left text-[20px] leading-none font-bold tracking-[-0.01em] whitespace-nowrap ${isDark ? 'text-(--foreground-primary-default)' : 'text-foreground'} ${labelFade}`}
          >
            {props.appName}
          </button>
        )}
        {/* Expanded only: visible collapse button, pinned to the right. */}
        {!collapsed && props.onToggleCollapse && (
          <button
            type="button"
            onClick={props.onToggleCollapse}
            aria-label="Collapse sidebar"
            // Exempt from the public share view's click interception — pure
            // layout. See the aside's `data-public-allowed` comment above.
            data-public-allowed=""
            className={`flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors ${labelFade} ${
              isDark
                ? 'text-(--foreground-subtle) hover:bg-(--foreground-secondary-default)/50 hover:text-(--foreground-primary-default)'
                : 'text-muted-foreground hover:bg-stone-200 hover:text-foreground'
            }`}
          >
            <SidebarSimple size={20} strokeWidth={1.6} />
          </button>
        )}
      </div>

      {/* ── Search — single bordered field; clips to its magnifier collapsed ── */}
      {props.onSearch &&
        (() => {
          const searchBtn = (
            <button
              type="button"
              onClick={props.onSearch}
              aria-label="Search"
              className={`flex h-10 w-full items-center overflow-hidden rounded-lg border transition-colors ${
                // `!` on both: globals.css's unlayered `* { border-color: var(--border) }`
                // otherwise wins over these arbitrary-var border-color utilities.
                isDark
                  ? 'border-(--stone-700)! bg-(--foreground-secondary-default)/30 text-(--foreground-subtle) hover:border-(--foreground-subtle)/50!'
                  : 'border-border bg-muted text-muted-foreground hover:border-foreground/30'
              }`}
            >
              <span className={ICON_ZONE}>
                <MagnifyingGlass size={18} weight="regular" />
              </span>
              <span className={`whitespace-nowrap text-body-sm-regular ${labelFade}`}>{props.searchPlaceholder ?? 'Search'}</span>
              <kbd
                className={`mr-3 ml-auto rounded-md border px-1.5 py-0.5 text-caption-regular ${labelFade} ${
                  isDark
                    ? 'border-(--stone-700)! bg-(--foreground-secondary-default)/50 text-(--foreground-subtle)'
                    : 'border-border bg-card text-muted-foreground'
                }`}
              >
                ⌘K
              </kbd>
            </button>
          );
          return (
            <div className="px-3 pb-2">
              {collapsed ? (
                <SimpleTooltip content={props.searchPlaceholder ?? 'Search'} side="right">
                  {searchBtn}
                </SimpleTooltip>
              ) : (
                searchBtn
              )}
            </div>
          );
        })()}

      {/* ── Optional back link ─────────────────────────────────────────────── */}
      {props.onNavigateBack && !collapsed && (
        <div className="px-3 pb-1">
          <button
            type="button"
            onClick={props.onNavigateBack}
            className={`flex h-9 w-full items-center overflow-hidden rounded-lg text-body-md-regular font-medium transition-colors ${
              isDark
                ? 'text-(--foreground-subtle) hover:text-(--foreground-primary-default)'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className={ICON_ZONE}>
              <CaretLeft size={20} strokeWidth={1.5} />
            </span>
            <span className="whitespace-nowrap">Back{props.backLabel ? ` to ${props.backLabel}` : ''}</span>
          </button>
        </div>
      )}

      {/* Stacked rail only (Figma "Admin main side bar"): a hairline divider
          directly under the logo, before the nav. */}
      {stacked && (
        // Figma stroke is 2px `Beige/900` (#393631) — NOT --stone-700
        // (#443f3a), a different token that only looks similar.
        <div data-testid="sidebar-logo-divider" className="mx-auto h-[2px] w-[42px] shrink-0 bg-(--beige-900)" aria-hidden />
      )}

      {/* ── Primary nav ──────────────────────────────────────────────────────
          Figma: 24px gap between the logo and the first nav item (brand block
          pb 16px + container gap 8px). The logo is centred in the 52px header
          (~10px slack below the glyph), so pt-3.5 (14px) lands the visible gap
          at ~24px. */}
      <nav
        className={
          stacked
            ? `flex flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto px-2 pt-3 pb-1 scrollbar-none [&::-webkit-scrollbar]:hidden`
            : 'flex flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto px-3 pt-3.5 pb-1 scrollbar-none [&::-webkit-scrollbar]:hidden'
        }
      >
        {props.navItems.map(renderNavItem)}
      </nav>

      {/* ── Footer: primary CTA + secondary nav + account ──────────────────── */}
      {/* Figma root padding-bottom is 16px (pb-4), not 12px. */}
      <div className={`mt-auto flex shrink-0 flex-col gap-4 ${stacked ? 'px-2 pb-4' : 'p-3'}`}>
        {props.primaryAction &&
          (() => {
            const Icon = props.primaryAction.icon;
            const btn = (
              <button
                type="button"
                onClick={props.primaryAction.onClick}
                className="relative flex h-10 w-full items-center overflow-hidden rounded-full bg-primary text-body-sm-medium text-white transition-colors hover:bg-primary/90"
              >
                <span className={ICON_ZONE}>
                  <Icon size={18} strokeWidth={1.8} />
                </span>
                <span className={`whitespace-nowrap ${labelFade}`}>{props.primaryAction.label}</span>
              </button>
            );
            return collapsed ? (
              <SimpleTooltip content={props.primaryAction.label} side="right">
                {btn}
              </SimpleTooltip>
            ) : (
              btn
            );
          })()}

        {props.footerItems?.map(renderNavItem)}

        {/* Account chip — avatar in the fixed icon zone, name/role clips (or,
            in the stacked rail, never has room to show at all — Figma's own
            "Sidebar / Avatar" spec is avatar-only there, but since there's no
            toggle to ever reveal the real name/role, they ride along in the
            accessible name instead of vanishing outright). */}
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`relative mt-1 flex h-10 w-full items-center overflow-hidden rounded-xl transition-colors ${
                isDark
                  ? 'bg-(--foreground-secondary-default)/30 hover:bg-(--foreground-secondary-default)/50'
                  : 'bg-muted hover:bg-accent'
              } ${stacked ? 'justify-center' : ''}`}
              aria-label={
                stacked && props.userName && props.userRole
                  ? `Account menu — ${props.userName}, ${props.userRole}`
                  : 'Account menu'
              }
              title={stacked && props.userName && props.userRole ? `${props.userName} · ${props.userRole}` : undefined}
            >
              <span className={ICON_ZONE}>
                <span
                  className={`flex size-10 items-center justify-center overflow-hidden rounded-full border text-caption-medium ${
                    isDark
                      ? 'border-(--stone-700)! bg-(--foreground-secondary-default) text-(--foreground-primary-default)'
                      : 'border-border bg-muted text-stone-700'
                  }`}
                >
                  {props.userInitials}
                </span>
              </span>
              {/* Stacked rail: name/role never actually show (no toggle ever
                  reveals them — see the aria-label/title above, which carry
                  them instead) but as a flex-1 filler this was stealing the
                  button's free space and pushing the avatar left instead of
                  letting `justify-center` above center it. `hidden` removes
                  its footprint. */}
              {!stacked && (
                <div className={`flex min-w-0 flex-1 flex-col pr-2 text-left whitespace-nowrap ${labelFade}`}>
                  {props.userName !== undefined ? (
                    <span
                      className={`truncate text-body-sm-semibold leading-tight ${isDark ? 'text-(--foreground-primary-default)' : 'text-foreground'}`}
                    >
                      {props.userName}
                    </span>
                  ) : (
                    <div className={`h-4 w-24 animate-pulse rounded-sm ${isDark ? 'bg-(--foreground-secondary-default)/40' : 'bg-muted'}`} />
                  )}
                  {props.userRole !== undefined ? (
                    <span
                      className={`mt-0.5 truncate text-caption-regular ${isDark ? 'text-(--foreground-subtle)' : 'text-muted-foreground'}`}
                    >
                      {props.userRole}
                    </span>
                  ) : (
                    <div className={`mt-1 h-3 w-16 animate-pulse rounded-sm ${isDark ? 'bg-(--foreground-secondary-default)/40' : 'bg-muted'}`} />
                  )}
                </div>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="z-30 w-52 p-1">
            <button
              type="button"
              className="flex h-9 w-full items-center gap-2.5 rounded-md px-3 text-body-sm-regular text-foreground hover:bg-accent"
              onClick={() => {
                setPopoverOpen(false);
                props.onNavigateProfile();
              }}
            >
              View profile
            </button>
            <div className="my-1 h-px bg-muted" />
            <div className="flex items-center gap-2 px-3 py-1">
              <Link to="/privacy-policy" className="text-caption-regular text-muted-foreground hover:text-foreground">
                Privacy
              </Link>
              <span className="text-caption-regular text-muted-foreground">·</span>
              <Link to="/terms" className="text-caption-regular text-muted-foreground hover:text-foreground">
                Terms
              </Link>
            </div>
            <div className="my-1 h-px bg-muted" />
            <button
              type="button"
              className="flex h-9 w-full items-center gap-2.5 rounded-md px-3 text-body-sm-regular text-rose-600 hover:bg-rose-50"
              onClick={() => {
                setPopoverOpen(false);
                void props.onSignOut();
              }}
            >
              <SignOut size={14} />
              Sign out
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </aside>
  );
}
